type TransformFunctionType = { functionName: string };

/**
 * Create a Map to store the API field names that
 * point to the corresponding function to execute.
 */
export const carDataFieldTransforms = new Map([
  ['upperCase', upperCase],
  ['capitalizeFirstLetter', capitalizeFirstLetter],
  ['addSuffixCubicMetres', addSuffixCubicMetres],
  ['autonomousDriveLevel', autonomousDriveLevel],
  ['getYearFromDatestamp', getYearFromDatestamp],
  ['addSuffixGramsPerKM', addSuffixGramsPerKM],
  ['regularServiceInterval', regularServiceInterval],
  ['transmissionType', transmissionType],
  ['fuelAddLitres', fuelAddLitres],
  ['fuelAddLitresPerKM', fuelAddLitresPerKM],
  ['toInteger', toInteger],
  ['titleCase', titleCase],
  ['driveLayout', driveLayout],
  ['toOneDecimal', toOneDecimal],
  ['tyresFormat', tyresFormat],
  ['addSuffixCC', addSuffixCC],
  ['addSuffixKG', addSuffixKG],
  ['addSuffixSeconds', addSuffixSeconds],
  ['addSuffixKMH', addSuffixKMH],
  ['addSuffixKM', addSuffixKM],
  ['addSuffixMM', addSuffixMM],
  ['addSuffixKWH', addSuffixKWH],
  ['addSuffixKW', addSuffixKW],
  ['addSuffixMonths', addSuffixMonths],
  ['addSuffixDegrees', addSuffixDegrees],
  ['ancapStars', ancapStars],
  ['rateOfCharge', rateOfCharge],
  ['addInchesSymbolToNumber', addInchesSymbolToNumber],
  ['tyreSpeedRating', tyreSpeedRating],
  ['numberWithCommas', numberWithCommas],
]);

//
// Note to self
//
// After looking at the excel spreadsheet, I think it will mostly make sense to create
// unique function names for most of the data points in the spreadsheet. There is too
// much complexity with the specific transforms and corresponding data points.
//
// I suggest we continue creating generic helper functions to assist the transforms,
// (eg) upperCase, addSuffix, capitalize, but the main use of the Map will be for the
// more complex fn transforms coming from SC (eg) transmissionType, acceleration.
//
// The Map fn will be big, but if we try and build a generic function to rule them all
// it will become complex, difficult to read and maintain.
//
// I recommend we aim to reduce the amount of transform combinations we use in SiteCore
// (eg) 'transmissionType' instead of 'transmissionType|upperCase|suffixKMS'.
// Then the 'transmissionType' fn can handle uppercasing and suffixing on its own.
//
//
//

/**
 * Transform a vehicle's data using a specific transform function registered in the transform Map.
 *
 * This function takes in a vehicle data object, an array of the field keys in the data to transform.
 *
 * @param vehicle The vehicle data object
 * @param fieldKeys An array of vehicle data object keys to target
 * @param transformFunction The transform function to run (string of funcs separated by the '|' pipe symbol)
 *
 * @returns The transformed data value
 */
export function transformDataPoint(vehicle: any, fieldKeys: string[], transformFunctions: TransformFunctionType[]) {
  // Loop over each transformFunction and execute the corresponding transform
  function recursiveTransform(transformFunctions: TransformFunctionType[], result: any): any {
    const fieldValue = result || vehicle[fieldKeys[0]];

    if (!fieldValue) {
      return handleUndefinedFields(fieldKeys);
    }

    if (transformFunctions.length === 0) {
      return result;
    }

    const transformFunc = transformFunctions[0].functionName;
    const fn = carDataFieldTransforms.get(transformFunc);
    const isValidFunction = carDataFieldTransforms.has(transformFunc) && fn && typeof fn === 'function';

    if (isValidFunction) {
      if (!fieldValue) {
        console.error(`no field value (${transformFunc}): ${JSON.stringify({ fieldValue, fieldKeys, value: result })}`);
      }

      return recursiveTransform(transformFunctions.slice(1), fn(vehicle, fieldKeys, result));
    }

    throw Error(`Transform function does not exist: ${JSON.stringify({ transformFunc, transformFunctions })}`);
  }

  return recursiveTransform(transformFunctions, undefined);
}

// --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// Transformation functions to render vehicle datapoints
// in comparison table, car details and other places.
// --- --- --- --- --- --- --- --- --- --- --- --- --- ---

/**
 * Uppercase a data field string.
 *
 * @param vehicle
 * @param fieldKeys
 *
 * @returns {String}
 */
function upperCase(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return fieldValue?.toUpperCase();
}

/**
 * Capitalise the first letter of a data string.
 *
 * @param vehicle
 * @param fieldKeys
 *
 * @returns {String}
 */
function capitalizeFirstLetter(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return fieldValue?.charAt(0)?.toUpperCase() + fieldValue?.slice(1);
}

/**
 * Format a string to title case.
 * For example: 'hello world' => 'Hello World'
 *
 * @param vehicle
 * @param fieldKeys
 *
 * @returns {String}
 */
function titleCase(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue: string = value || vehicle[fieldKeys[0]];

  return fieldValue
    .split(' ')
    .map((w) => w[0].toUpperCase() + w.substring(1).toLowerCase())
    .join(' ');
}

/**
 * Transform the transmission type data into a human readable string.
 *
 * @param vehicle
 * @param fieldKeys
 *
 * @returns {String}
 */
function transmissionType(vehicle: any, fieldKeys: string[], value: string): string {
  const transType = capitalizeFirstLetterOfString(vehicle[fieldKeys[1]]); // manual -> Manual, auto -> Auto
  const speeds = capitalizeFirstLetterOfString(vehicle[fieldKeys[0]]); // variable -> Variable, 3 -> 3 (i.e. no change for numbers)
  return speeds + ' speed ' + transType;
}

/**
 * Transform the EV charge data into a readable string.
 *
 * @param vehicle
 * @param fieldKeys
 *
 * @returns {String}
 */
function rateOfCharge(vehicle: any, fieldKeys: string[], value: string): string {
  //
  //
  return 'rate of charge TBD';
}

/**
 * Format a string to show the servicing interval in KMs and months.
 *
 * @param vehicle
 * @param fieldKeys
 *
 * @returns {String}
 */
function regularServiceInterval(vehicle: any, fieldKeys: string[], value: string): string {
  const kmsFormatted = addSuffix(numberWithCommas(vehicle, fieldKeys, value), 'km');
  const monthsFormatted = addSuffix(vehicle[fieldKeys[1]], ' months');

  return `${kmsFormatted} ${monthsFormatted ? `or every ${monthsFormatted}` : ''}`;
}

/**
 * Add commas to a number based on locale standards
 * eg. 10000 => 10,000
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 * @returns {String}
 */
function numberWithCommas(vehicle: any, fieldKeys: string[], value: string): string {
  return new Intl.NumberFormat().format(vehicle[fieldKeys[0]]);
}

/**
 * Add suffix 'L' suffix to value.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function fuelAddLitres(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  if (!fieldValue) return 'N/A';

  return addSuffix(fieldValue, 'L');
}

/**
 * Add suffix 'L/100km' to value.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function fuelAddLitresPerKM(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'L/100km');
}

/**
 * Add suffix 'g/km' to value.
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixGramsPerKM(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'g/km');
}

/**
 * Remove decimal places from an integer and return as a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function toInteger(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return String(Math?.trunc(Number(fieldValue)));
}

/**
 * Return a number as a string to one decimal place.
 * For example: 2.20 => 2.2
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 * @returns
 */
function toOneDecimal(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return String(Number(Number(fieldValue))?.toFixed(1));
}

/**
 * Returns a lookup and transform of drive layouts
 * For example: front = FWD
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 * @returns
 */
function driveLayout(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  switch (fieldValue) {
    case 'front':
      return 'FWD';
    case 'rear':
      return 'RWD';
    case '4x4':
      return '4WD';
    default:
      return 'Unknown drive layout';
  }
}

/**
 * Add 'cc' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixCC(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'cc');
}

/**
 * Add 'kg' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixKG(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'kg');
}

/**
 * Add 's' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixSeconds(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 's');
}

/**
 * Add 'km/h' to the end of a string.
 *<sup>
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixKMH(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'km/h');
}

/**
 * Add 'km' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixKM(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'km');
}

/**
 * Add 'mm' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixMM(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'mm');
}

/**
 * Add 'm3' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixCubicMetres(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'm<sup>3</sup>');
}

/**
 * Add 'kWh' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixKWH(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'kWh');
}

/**
 * Add 'kW' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixKW(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, 'kW');
}

/**
 * Add ' months' to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixMonths(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];
  // TODO: confirm that we want a space before the word 'months'
  return addSuffix(fieldValue, ' months');
}

/**
 * Add degrees HTML entity to the end of a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addSuffixDegrees(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return addSuffix(fieldValue, '&deg;');
}

/**
 * Format a string into tyres format
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function tyresFormat(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return fieldValue?.replaceAll('_', '/');
}

/**
 * Return the automation level from a given number.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function autonomousDriveLevel(vehicle: any, fieldKeys: string[], value: string | number): string {
  // TODO: confirm that these are all possible options for autonomous drive level
  switch (String(value || vehicle[fieldKeys[0]])) {
    case '1':
      return '1 - Full automation';
    case '2':
      return '2 - Partial automation';
    default:
      return 'Unknown autonomous drive level';
  }
}

/**
 * Returns the the ANCAP rating as a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function ancapStars(vehicle: any, fieldKeys: string[], value: string | number): string {
  const fieldValueAsNumber = Number(value || vehicle[fieldKeys[0]]);

  return Math?.trunc(fieldValueAsNumber) + ' Stars';
}

/**
 * Return the year from a given date stamp.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function getYearFromDatestamp(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValueAsDate = new Date(value || vehicle[fieldKeys[0]]);

  return String(fieldValueAsDate?.getFullYear());
}

/**
 * Add the inches (") symbol to a number in a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 *
 * @returns {String}
 */
function addInchesSymbolToNumber(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];

  return fieldValue.replace(/(\d+)/, '$1"');
}

/**
 * Create a dictionary for speed symbols.
 *
 * @param vehicle
 * @param fieldKeys
 * @param value
 * @returns {String}
 */
function tyreSpeedRating(vehicle: any, fieldKeys: string[], value: string): string {
  const fieldValue = value || vehicle[fieldKeys[0]];
  let output = '';

  switch (fieldValue) {
    case 'F':
      output = '80';
      break;
    case 'G':
      output = '90';
      break;
    case 'J':
      output = '100';
      break;
    case 'K':
      output = '110';
      break;
    case 'L':
      output = '120';
      break;
    case 'M':
      output = '130';
      break;
    case 'N':
      output = '140';
      break;
    case 'P':
      output = '150';
      break;
    case 'Q':
      output = '160';
      break;
    case 'R':
      output = '170';
      break;
    case 'S':
      output = '180';
      break;
    case 'T':
      output = '190';
      break;
    case 'U':
      output = '200';
      break;
    case 'H':
      output = '210';
      break;
    case 'V':
    case 'Z':
      output = '240';
      break;
    case 'W':
      output = '270';
      break;
    case 'ZR':
      output = 'Above 270';
      break;
    case 'Y':
      output = '300';
      break;
    case '(Y)':
      output = 'Above 300';
      break;
    default:
      return 'N/A';
  }

  return addSuffix(fieldValue + ' - ' + output, 'km/h');
}

// --- --- --- --- --- --- --- --- --- --- --- ---
// Generic Helper Functions
// --- --- --- --- --- --- --- --- --- --- --- ---

/**
 * Add a suffix to a string.
 *
 * @param vehicle
 * @param fieldKeys
 * @param suffixValue
 *
 * @returns {String}
 */
function addSuffix(value: string, suffixValue: string): string {
  return value ? value + suffixValue : '';
}

/**
 * Capitalise the first letter of a string parameter.
 *
 * @param value
 *
 * @returns {String}
 */
function capitalizeFirstLetterOfString(value: string): string {
  return value?.charAt(0)?.toUpperCase() + value?.slice(1);
}

/**
 * Helper function to handle specific error scenarios.
 *
 * @param fieldKeys An array of vehicle data object keys to target
 *
 * @returns An informative error
 */
function handleUndefinedFields(fieldKeys: string[]) {
  if (fieldKeys[0] === 'ANCAP_Overall_score') {
    return 'Current rating not available';
  }

  return 'N/A';
}
